import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

import { EMPTY_STRING } from '@e-socna/empty';
import type { SectionTypeId } from '@e-socna/data-layer';

import { SectionTemplateInfoForm } from '../../forms/SectionTemplateInfo';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { RightDrawer } from '../../components/RightDrawer';
import { Code } from '../../components/Code';
import { useDocumentEditorContext } from '../../components/DocumentEditor/contexts/DocumentEditorContext/hooks/useDocumentEditorContext.hook';
import { PATH_NOT_FOUND } from '../../app/constants/paths.constant';

import type { SectionTemplatePage_OneSectionTemplate } from './hooks/useFindOneSectionTemplateQuery.hook';

export const SectionTemplateInfoTabPanel = () => {
  const {
    props: {
      routerSettings: { editorModePaths },
    },
    state,
    actions,
  } = useDocumentEditorContext<SectionTemplatePage_OneSectionTemplate>();

  const navigate = useNavigate();
  const { pathname: currentPath } = useLocation();
  const { sectionTypesIdMap } = useReferencesContext();

  const leaveEditionMode = useCallback(
    () => void navigate(editorModePaths.Info ?? PATH_NOT_FOUND),
    [editorModePaths.Info, navigate],
  );

  const sectionTemplate = state.editable;
  const { codingConfig, code, label, typeId } = sectionTemplate;

  const useSubmitFactory = useCallback(
    (callback?: () => void) =>
      (values: SectionTemplatePage_OneSectionTemplate) => {
        actions.updateInfo(values);
        callback?.();
      },
    [actions],
  );

  const sectionTypeLabel =
    sectionTypesIdMap[typeId as SectionTypeId]?.label ?? 'Inconnu';

  return (
    <>
      <Stack spacing={2} sx={{ pt: 4, width: '100%' }}>
        <Typography gutterBottom variant="h6">
          Informations Générales
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Code code={code} codingConfig={codingConfig} />
            <Typography gutterBottom variant="body1">
              Libellé : {label === EMPTY_STRING ? 'Non défini' : label}
            </Typography>
            <Typography gutterBottom variant="body1">
              Type de section : {sectionTypeLabel}
            </Typography>
          </Box>
        </Paper>
      </Stack>
      <RightDrawer
        isOpen={currentPath === editorModePaths.InfoEdit}
        onDrawerClose={leaveEditionMode}
      >
        <SectionTemplateInfoForm
          initialValues={sectionTemplate}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          showCancelButton
          title="Informations Générales"
          useSubmitFactory={useSubmitFactory}
        />
      </RightDrawer>
    </>
  );
};
